<script>
  // The explicit per-row decision (spec §5): [Set as <target> |▾] [Do not
  // adjust]. Rows start undecided; clicking a selected button un-decides it,
  // and picking a status from the caret implies the adjust decision.
  //
  // The Set-as cluster is green-tinted so the suggested action reads as
  // primary. Rows with no default target — outside-scope rows, and misses when
  // the miss action is "Leave Untouched" — get an explicit "Choose status…"
  // select instead of a disabled mystery button.
  import { ALL_STATUSES, statusLabel } from "./engine";
  export let decision = null; // 'adjust' | 'skip' | null (undecided)
  export let target = null; // effective target status (override ?? group default)
  export let onDecide; // ('adjust' | 'skip' | null) => void
  export let onTarget; // (status) => void — picking a status implies 'adjust'

  function pickStatus(e) {
    if (e.target.value) onTarget(e.target.value);
    e.target.value = "";
  }
</script>

<div class="recon-decision" role="group">
  {#if target}
    <button
      class="rd-btn rd-set"
      class:selected={decision === "adjust"}
      on:click={() => onDecide(decision === "adjust" ? null : "adjust")}
    >
      <!-- text checkmark, not an FA <i>: dom.watch() svg-replacement detaches
           nodes Svelte owns and crashes teardown of conditional blocks -->
      {decision === "adjust" ? "✓ " : ""}Set as {statusLabel(target)}
    </button>
    <select
      class="rd-caret"
      class:selected={decision === "adjust"}
      title="Set as a different status"
      value=""
      on:change={pickStatus}
    >
      <option value="" disabled selected hidden></option>
      {#each ALL_STATUSES as s}
        <option value={s}>Set as {statusLabel(s)}</option>
      {/each}
    </select>
  {:else}
    <!-- no suggested status by design: changing one of these is an explicit,
         opt-in pick -->
    <select class="rd-choose" title="No suggested status — pick one to change this unit" value="" on:change={pickStatus}>
      <option value="" disabled selected hidden>Choose status…</option>
      {#each ALL_STATUSES as s}
        <option value={s}>Set as {statusLabel(s)}</option>
      {/each}
    </select>
  {/if}
  <button
    class="rd-btn rd-skip"
    class:selected={decision === "skip"}
    on:click={() => onDecide(decision === "skip" ? null : "skip")}
  >
    {decision === "skip" ? "✓ " : ""}Do not adjust
  </button>
</div>

<style>
  .recon-decision {
    display: inline-flex;
    align-items: stretch;
    flex: 0 0 auto;
  }
  .rd-btn {
    font-size: 0.8125rem;
    line-height: 1.4;
    padding: 0.25rem 0.625rem;
    white-space: nowrap;
    flex: 0 0 auto;
  }
  /* primary (suggested) action: green-tinted even when unselected */
  .rd-set {
    background: #e9f6f0;
    border: 1px solid #9ed3ba;
    color: #146c43;
    font-weight: 600;
    border-radius: 0.375rem 0 0 0.375rem;
  }
  .rd-set:hover:not(.selected) {
    background: #d8efe3;
  }
  .rd-set.selected {
    background: #198754;
    border-color: #198754;
    color: #fff;
  }
  /* native select dressed as the split-button caret, flush against Set-as */
  .rd-caret {
    width: 1.75rem;
    flex: 0 0 auto;
    margin-left: -1px;
    border: 1px solid #9ed3ba;
    border-radius: 0 0.375rem 0.375rem 0;
    background: #e9f6f0;
    color: #146c43;
    cursor: pointer;
    padding: 0;
  }
  .rd-caret.selected {
    background: #198754;
    border-color: #198754;
    color: #fff;
  }
  /* explicit no-default affordance: dashed invite, reads "pick one" not "broken" */
  .rd-choose {
    font-size: 0.8125rem;
    line-height: 1.4;
    padding: 0.25rem 0.5rem;
    flex: 0 0 auto;
    border: 1px dashed #9ed3ba;
    border-radius: 0.375rem;
    background: #fff;
    color: #146c43;
    font-weight: 600;
    cursor: pointer;
  }
  .rd-choose:hover {
    background: #e9f6f0;
  }
  /* secondary action: quiet, separated from the Set-as cluster */
  .rd-skip {
    background: #fff;
    border: 1px solid #ced4da;
    color: #6c757d;
    border-radius: 0.375rem;
    margin-left: 0.5rem;
  }
  .rd-skip:hover:not(.selected) {
    background: #f1f3f5;
  }
  .rd-skip.selected {
    background: #6c757d;
    border-color: #6c757d;
    color: #fff;
  }
</style>
